/**
 * Payroll engine — public surface. Pure JS, no I/O, no Node builtins:
 * imported by Cloud Functions (runs/backtests) AND the frontend calculator
 * via src/pages/Payroll/engineClient.js (one engine, one code path).
 */

const { ENGINE_VERSION, PARITY_BASE } = require('./version');
const numeric = require('./numeric');
const { TAGS, resolveDealSource } = require('./tags');
const { shareOf } = require('./shareOf');
const { resolvePricingContext } = require('./pricingContext');
const {
  buildContext,
  matchSplitRule,
  matchFeeOverride,
  calculateCommission,
  evaluateRow,
  routePayees,
  buildCdCounts,
} = require('./calculate');

/**
 * Flag vocabulary (Marilyn's interface — preserve exactly; see PLAN.md §5.6).
 * `:x` suffixed flags carry a detail payload after the colon.
 */
const FLAG_VOCABULARY = {
  MANUAL_PREFILLED: 'Primary Commission was already filled in — engine did not calculate; routed as-is.',
  'UNMATCHED_LO:email': 'No LO Comp Profile found for this email.',
  'PROFILE_INCOMPLETE:email': 'Profile status is VERIFY — rates not confirmed.',
  HOUSE_DEAL: 'House account — primary $0, pay via Secondary.',
  HOUSE_SECONDARY_EMPTY: 'House account with no manual Secondary amount.',
  SKIPPED_NO_PROFILE: 'Row skipped — no profile for the primary LO.',
  MISSING_CORE_DATA: 'Row skipped — loan amount / revenue non-numeric or lender blank.',
  SOURCE_ASSUMED_COMPANY: 'No Deal Source column value or comp-code tag — assumed COMPANY from the DTC plan.',
  SOURCE_TAG_CONFLICT: 'Comp code contains both company-deal and self-sourced tags — treated as COMPANY.',
  'LENDER_NOT_IN_MARGIN_TAB:name': 'No margin row for this lender/investor.',
  'MARGIN_COLUMN_MISSING:row:col': 'Branch margin column blank — used the other column as fallback.',
  BP_TAG_OVERRIDDEN_BY_MARGIN: 'Comp code says BP, but margin data says LP — margin wins.',
  BP_TAG_IGNORED_NO_MARGIN: 'Comp code says BP but the lender has no margin row — pricing cannot be verified from revenue; defaulted to LP (D-50). Add the lender/investor to the Margin tab.',
  BROKER_REF_FLAT: 'Broker-referral flat rate applied.',
  CD_FLAT_APPLIED: 'Company-deal flat rate applied.',
  CD_RATES_MISSING: 'Company deal but no CD rates on the profile — self-sourced rates used as fallback.',
  MAX_COMP_APPLIED: 'Lender Max Comp cap reached — fallback % of revenue paid.',
  BP_FLOOR_APPLIED: 'BP revenue floor reduced the comp.',
  SPLIT_SECONDARY_MISSING: 'Split rule wanted a secondary, but the row has no Secondary LO — rule not applied.',
  'SECONDARY_UNHANDLED:email': 'Secondary LO present but no split rule, LOA schedule, or manual amount covers them.',
  INSURANCE_NOTE: 'Comp code mentions an insurance deduction — handled manually (flag only).',
  EPO_NOTE: 'Comp code mentions an EPO — handled manually (flag only).',
  REFERRAL_NOTE: 'Comp code mentions a referral — review manually (flag only).',
  TOLERANCE_NOTE: 'Comp code mentions tolerance — review manually (flag only).',
  FLOATING_PRICE: 'Comp code says floating / subject to change.',
  MANUAL_MINUS_NOTE: 'Comp code contains a "minus" note — review manually (flag only).',
  TIER_TRUEUP: 'Tier-2 retro adjustment payee row.',
  TIER_LEGACY_CHECK: 'Tier 2 hit, but same-month rows were paid under the LEGACY process — verify manually.',
  TIER2_NO_BATCH_COL: 'Tier 2 hit but the Payroll Batch column does not exist — true-ups skipped.',
  HANNAH_RULE_CHECK: 'Hannah split rule fired — verify per agreement.',
  FOSTER_NOTE: "Foster's split note found on a manual row — payee emitted.",
  BRANCH_UNKNOWN: 'RESERVED (D-25): profile has no Branch — margin column fell back to the W2 proxy.',
};

module.exports = {
  ENGINE_VERSION,
  PARITY_BASE,
  FLAG_VOCABULARY,
  TAGS,
  resolveDealSource,
  shareOf,
  resolvePricingContext,
  buildContext,
  matchSplitRule,
  matchFeeOverride,
  calculateCommission,
  evaluateRow,
  routePayees,
  buildCdCounts,
  ...numeric,
};
